import React from 'react';
import { APP_STORE_URL, CTA_LABEL, CTA_MICROLINE } from './constants';

/* ---------------------------------------------------------------- GOLD CTA */

/**
 * GoldCTA — the primary conversion surface, and the one gold moment on a
 * screenful. Gold → Gold Deep, Indigo Canvas label, press 0.98.
 *
 * Gold is scarce by law: at most one gold CTA plus one gold type accent per
 * section. If a section already spends its gold on an outline headline or a
 * numeral, it does not also get a gold CTA.
 */
export function GoldCTA({
  href = APP_STORE_URL,
  children = CTA_LABEL,
  className = '',
  fullWidth = false,
  ariaLabel = `${CTA_LABEL} — download CrossedPaths on the App Store`,
  'data-testid': testId,
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      data-testid={testId}
      className={`
        inline-flex items-center justify-center rounded-full
        px-7 py-4 text-[15px] font-bold tracking-[0.01em] text-canvas
        transition-[box-shadow,transform] duration-150 ease-reveal
        hover:-translate-y-px hover:shadow-[0_10px_40px_rgba(255,197,61,0.45)]
        active:scale-[0.98]
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px]
        focus-visible:outline-gold-300
        ${fullWidth ? 'w-full max-w-[340px]' : ''}
        ${className}
      `}
      style={{
        fontFamily: 'var(--font-body)',
        background: 'linear-gradient(135deg, #FFC53D 0%, #D9A226 100%)',
        boxShadow: '0 8px 32px rgba(255, 197, 61, 0.28)',
      }}
    >
      {children}
    </a>
  );
}

/** The verified microline that sits under every primary CTA. */
export function CtaMicroline({ className = '' }) {
  return (
    <p className={`cp-mono text-[length:var(--fs-mono)] ${className}`}>{CTA_MICROLINE}</p>
  );
}

/* ------------------------------------------------------------ STORE BADGE */

/**
 * Apple's official "Download on the App Store" artwork, from Apple's
 * marketing-tools API. Its intrinsic viewBox is 119.66×40.
 *
 * IT SHIPS UNMODIFIED. Apple's identity guidelines prohibit recreating or
 * restyling the badge, and that covers more than the drawing: no border, no
 * glow, no recolour, no filter, no backdrop, and none of our own words inside
 * a badge-shaped element. It is designed to sit on dark — it needs no help.
 * Any interaction affordance belongs on the wrapper, never on the artwork.
 *
 * An earlier revision of this file hand-drew the badge. That was a violation
 * on both counts (a recreation, and restyled with a hairline border); it does
 * not come back.
 */
export const OFFICIAL_BADGE_SRC = '/assets/app-store-badge.svg';

const BADGE_H = 40;
const BADGE_W = 119.66407;

/**
 * AppStoreBadge — the secondary conversion surface beside the gold pill.
 * The anchor carries the accessible name and the focus ring; the artwork
 * inside is untouched.
 */
export function AppStoreBadge({ href = APP_STORE_URL, className = '', 'data-testid': testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download on the App Store"
      data-testid={testId}
      className={`
        inline-flex shrink-0 items-center no-underline
        transition-transform duration-150 ease-reveal
        hover:-translate-y-px active:scale-[0.98]
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[6px]
        focus-visible:outline-gold-400
        ${className}
      `}
    >
      <img
        src={OFFICIAL_BADGE_SRC}
        alt="Download on the App Store"
        width={BADGE_W}
        height={BADGE_H}
        decoding="async"
        className="block h-10 w-auto"
      />
    </a>
  );
}

export default GoldCTA;
